import cx from "classnames";
import React, { useCallback, useRef, useState } from "react";
import * as Scrivito from "scrivito";
import NiceModal from "@ebay/nice-modal-react";
import { useAppBaseContext } from "providers";
import { useProjectDataStore } from "stores/projectStore";
import { useProductsOverviewActions } from "stores/productsOverviewStore";
import {
  checkIsDummyProduct,
  findKeylistBySeriesId,
  getCategoryOrSeriesMap,
} from "utils";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { Unit } from "variables/enums";
import {
  ProductConfiguratorDownloads,
  ProductConfiguratorForm,
  ProductConfiguratorHeader,
  ProductConfiguratorLeftSidebar,
  ProductConfiguratorResult,
} from "./SubComponents";
import { LeftSidebarPortal } from "../LeftSidebar/LeftSidebarPortal";
import { XCircleLoader } from "../XCircleLoader/XCircleLoader";

import s from "./ProductConfiguratorComponent.module.scss";
import { useLoadProductState } from "./hooks/useLoadProductState";

export const ProductConfiguratorComponent = Scrivito.connect(
  ({
    nodeId,
    configuredSeriesIdOf,
    wizardType,
    dummyType,
    groupPosition,
    type,
    isModalView = false,
    isWizardView = false,
    forceLoadDefault = false,
  }) => {
    const isDummy = forceLoadDefault || checkIsDummyProduct(type);
    const { isSameProj } = useAppBaseContext();

    const { setProductCategoryId } = useProductsOverviewActions();

    const units = useProjectDataStore((state) => state.units) || Unit.de_m3ph;

    const params = Scrivito.currentPageParams();

    const { seriesId, productId } = params;

    const prevProductIdRef = useRef(null);

    const productStoreName = useProductConfiguratorStore.use.productName();
    const { productName: productNameFromParams } = Scrivito.currentPageParams();

    const productName = productNameFromParams || productStoreName;

    useLoadProductState({
      nodeId: nodeId ?? productId,
      seriesId: Number(seriesId || configuredSeriesIdOf),
      units,
      topologyData: {
        dummyType,
        wizardType,
        groupPosition,
        isDummy,
      },
      productName,
    });

    const allProducts = useAllProducts();
    const productData = useProductConfiguratorStore.use.productData();
    const activePart = useProductConfiguratorStore.use.activePart();
    const { setActivePartModal, setIsConfigurationModalOpened, setActivePart } =
      useProductConfiguratorStore.use.actions();

    const [, seriesMap] = allProducts && getCategoryOrSeriesMap(allProducts);

    const productKeyList = findKeylistBySeriesId({
      seriesMap,
      productSeriesId: seriesId,
    });

    if (productKeyList.length && nodeId) {
      setProductCategoryId(productKeyList);
    }

    if (productId && prevProductIdRef.current !== productId) {
      setProductCategoryId([]);
      prevProductIdRef.current = productId;
    }
    const [pageState, setPageState] = useState(
      isSameProj ? "product_specification" : "technical_design",
    );

    const modalBodyRef = useRef(null);

    const handleChangePageState = useCallback(() => {
      setPageState((page) =>
        page === "result" || page === "technical_design"
          ? "product_specification"
          : "technical_design",
      );
    }, [setPageState]);

    const activatePartItemRef = useRef(null);

    const handleConfigurationModal = useCallback(() => {
      setIsConfigurationModalOpened(true);
      NiceModal.show("product-configuration-modal", {
        activatePartItem: (part, e, isOpened) =>
          activatePartItemRef.current?.(part, e, isOpened),
      }).then(() => {
        setIsConfigurationModalOpened(false);
      });
    }, [setIsConfigurationModalOpened]);

    const activatePartItem = useCallback(
      (part, e, isConfigurationModalOpened) => {
        e.stopPropagation();
        if (
          pageState !== "product_specification" &&
          !isConfigurationModalOpened &&
          !isModalView
        ) {
          handleConfigurationModal();
        }
        if (
          (isModalView && pageState === "technical_design") ||
          pageState === "result"
        ) {
          handleChangePageState();
        }

        isConfigurationModalOpened
          ? setActivePartModal(part.key)
          : setActivePart(part.key);
      },
      [
        pageState,
        isModalView,
        setActivePartModal,
        setActivePart,
        handleConfigurationModal,
        handleChangePageState,
      ],
    );
    activatePartItemRef.current = activatePartItem;

    const hasInputsAndNotModal =
      productData?.product?.inputs?.length > 0 && !isModalView;

    if (!seriesId && !configuredSeriesIdOf) {
      return "No ID provided";
    }

    return (
      <div
        className={cx("flex-grow-1", {
          "h-100": hasInputsAndNotModal && pageState === "technical_design",
          "bg-gray-100": !isModalView,
          "h-100 bg-white": isModalView,
        })}
      >
        <div
          id="product-configurator-wrapper"
          className={cx("h-100", {
            product_configurator_modal: isModalView,
          })}
        >
          <div
            className={cx("px-7 mx-auto h-100", {
              "pb-1": !isModalView,
            })}
          >
            {productData ? (
              <>
                <ProductConfiguratorHeader
                  isModalView={isModalView}
                  isWizardView={isWizardView}
                  activatePartItem={activatePartItem}
                  activePart={activePart}
                  handleConfigurationModal={handleConfigurationModal}
                  setActivePart={setActivePart}
                  handleChangePageState={handleChangePageState}
                  pageState={pageState}
                  productName={productName}
                />
                <div
                  className={cx(
                    "overflow-auto scroll-smooth flex-grow-1 min-h-0 d-flex flex-column",
                    {
                      "px-2 pb-2": isModalView,
                      [s.overFlowHeight]: isModalView,
                    },
                  )}
                  ref={isModalView ? modalBodyRef : null}
                  style={{ height: "calc(-340px + 100vh)" }}
                >
                  {pageState === "product_specification" && (
                    <ProductConfiguratorForm
                      orderCode={productData?.product?.orderCode}
                      activePart={activePart}
                      isModalView={isModalView}
                      setActivePart={setActivePart}
                      modalBodyRef={modalBodyRef}
                    />
                  )}
                  {pageState === "technical_design" && (
                    <ProductConfiguratorResult
                      hasInputsAndNotModal={hasInputsAndNotModal}
                      isModalView={isModalView}
                    />
                  )}
                  {pageState === "downloads" && (
                    <ProductConfiguratorDownloads />
                  )}
                </div>
              </>
            ) : (
              <div className="d-flex align-items-center h-100 pt-8">
                <XCircleLoader />
              </div>
            )}
          </div>
        </div>
        {!isModalView && (
          <LeftSidebarPortal>
            <ProductConfiguratorLeftSidebar
              productName={productData?.product?.orderCode?.key}
              parts={productData?.product?.orderCode?.parts || []}
              activePart={activePart}
              setActivePart={setActivePart}
              activatePartItem={activatePartItem}
              pageState={pageState}
              setPageState={setPageState}
            />
          </LeftSidebarPortal>
        )}
      </div>
    );
  },
);
